import { arrayOf, string, shape, array, bool } from "prop-types";
import * as R from "ramda";

//types
export const pageShape = shape({
  pageTitle: string.isRequired,
  pageBreadcrumb: array.isRequired,
  pageGuid: string.isRequired,
});

export const sectionShape = shape({
  sectionCountry: string.isRequired,
  sectionCountryGuid: string.isRequired,
  sectionTopic: string.isRequired,
  sectionTopicGuid: string.isRequired,
  sectionPages: arrayOf(pageShape).isRequired,
});

export const reportShape = shape({
  reportTitle: string.isRequired,
  reportPages: arrayOf(pageShape).isRequired,
});

export const facetShape = shape({
  facetTitle: string.isRequired,
  facetGuid: string.isRequired,
});

export const facetGroupShape = shape({
  facetGroupTitle: string.isRequired,
  facetGroupValue: string.isRequired,
  facets: arrayOf(facetShape).isRequired,
});

export const initialStateShape = shape({
  draftReport: arrayOf(pageShape).isRequired,
  savedReports: arrayOf(reportShape).isRequired,
});

export const reportBuilderPropTypes = {
  isAuthenticated: bool.isRequired,
  initialState: initialStateShape,
  sections: arrayOf(sectionShape).isRequired,
  facetGroups: arrayOf(facetGroupShape).isRequired,
};

//functions

const rejectWithIndex = R.addIndex(R.reject);

export const reorderSavedReportPages = R.curry(
  (savedReportIndex, dragStartIndex, dragEndIndex, state) => {
    return R.over(
      R.lensPath(["savedReports", savedReportIndex, "reportPages"]),
      R.move(dragStartIndex, dragEndIndex),
      state,
    );
  },
);

export const reorderDraftReportPages = R.curry(
  (dragStartIndex, dragEndIndex, state) => {
    return R.over(
      R.lensProp("draftReport"),
      R.move(dragStartIndex, dragEndIndex),
      state,
    );
  },
);

export const toggleItemInArray = R.curry((value, array) => {
  if (Array.isArray(value)) {
    return R.symmetricDifference(value, array);
  } else {
    return R.symmetricDifference([value], array);
  }
});

export const updateDraftReportPages = R.curry((stagedPages, state) => {
  return R.assoc("draftReport", stagedPages, state);
});

export const updateSavedReportPages = R.curry(
  (savedReportIndex, stagedPages, state) => {
    return R.assocPath(
      ["savedReports", savedReportIndex, "reportPages"],
      stagedPages,
      state,
    );
  },
);

export const updateSavedReportTitle = R.curry((index, newTitle, state) => {
  return R.assocPath(["savedReports", index, "reportTitle"], newTitle, state);
});

export const newSavedReport = R.curry((title, index, pages, state) =>
  R.compose(
    updateSavedReportPages(index, pages),
    updateSavedReportTitle(index, title),
  )(state),
);

export const convertDraftReportToSavedReport = R.curry(
  (title, index, pages, state) => {
    return R.compose(
      updateDraftReportPages([]),
      newSavedReport(title, index, pages),
    )(state);
  },
);

export const removePagesFromDraftReport = R.curry((pages, state) => {
  return R.reduce(
    (acc, page) => removePageFromDraftReport(page, acc),
    state,
    pages,
  );
});

export const removePagesFromSavedReport = R.curry((index, pages, state) => {
  return R.reduce(
    (acc, page) => removePageFromSavedReport(index, page, acc),
    state,
    pages,
  );
});

export const removePageFromSavedReport = R.curry((index, page, state) => {
  return R.over(
    R.lensPath(["savedReports", index, "reportPages"]),
    R.reject(R.equals(page)),
    state,
  );
});

export const removePageFromDraftReport = R.curry((page, state) => {
  return R.over(R.lensProp("draftReport"), R.reject(R.equals(page)), state);
});

export const deleteSavedReport = R.curry((index, state) => {
  return R.over(
    R.lensProp(["savedReports"]),
    rejectWithIndex((item, curIndex) => curIndex === index),
    state,
  );
});
